/**
 * Smart Projections engine — pure functions extracted verbatim from
 * RADAR_Projection_Planner_Overall_Excel_Export.html.
 *
 * The source HTML is the contract; every function below preserves its
 * original behaviour so projections stay bit-identical to the audited
 * spreadsheet. DO NOT tweak formulas or thresholds.
 *
 * DOM references from the source (`document.getElementById(...)`) are
 * removed here — this file is a data-only kernel. The React page owns
 * all state (`sku`, `target`, `params`, `channelPct`, `weightMode`,
 * `newSkus`, `editedProjections`) and calls these functions with
 * explicit inputs.
 */

import { ACTUALS, ACTUAL_MONTHS, ALL_MONTHS } from './smart-projections-data';

// Runtime extension: when the Data Feeding Center saves an override for a
// month that isn't in the bundled ACTUAL_MONTHS array (e.g. AUG'26 became
// actual after ops fed data), we push it here so isNewOrSparseSku and
// baseMethodWeights recognise it as history. Order is preserved via
// ALL_MONTHS. Idempotent — no-op if the month is already registered.
export function registerActualMonth(month) {
  if (!month || ACTUAL_MONTHS.includes(month) || !ALL_MONTHS.includes(month)) return;
  const targetIdx = ALL_MONTHS.indexOf(month);
  // Insert in chronological order (ALL_MONTHS is chronological).
  let inserted = false;
  for (let i = 0; i < ACTUAL_MONTHS.length; i++) {
    if (ALL_MONTHS.indexOf(ACTUAL_MONTHS[i]) > targetIdx) {
      ACTUAL_MONTHS.splice(i, 0, month);
      inserted = true;
      break;
    }
  }
  if (!inserted) ACTUAL_MONTHS.push(month);
}

// ---- Formatting helpers ------------------------------------------------------
export const fmt = (n) => (Number.isFinite(n) ? Math.round(n).toLocaleString() : '-');
export const avg = (a) => {
  const x = a.filter(Number.isFinite);
  return x.length ? x.reduce((s, v) => s + v, 0) / x.length : 0;
};
export const monthIndex = (m) => ALL_MONTHS.indexOf(m);

// Actual value lookup — merges live ACTUALS with any user-added SKUs (which
// only exist in-memory until fed data arrives).
export const val = (sku, m, actualsOverride) => {
  const src = actualsOverride || ACTUALS;
  const v = src[sku]?.[m];
  return Number.isFinite(v) ? v : null;
};

export const sameLY = (m) => {
  const i = monthIndex(m);
  return i >= 12 ? ALL_MONTHS[i - 12] : null;
};

export const priorMonths = (m, n) => {
  const i = monthIndex(m);
  return ALL_MONTHS.slice(Math.max(0, i - n), i).filter((x) => ACTUAL_MONTHS.includes(x));
};

// ---- SKU categorisation & peer discovery (sparse SKU rule) ------------------
export function normalizeCategory(value) {
  const s = String(value || '').toLowerCase();
  if (s.includes('creatine')) return 'creatine';
  if (
    s.includes('pre-workout') || s.includes('pre workout') ||
    s.includes('dynamite')    || s.includes('warrior')     ||
    s.includes('beast mode')  || s.includes('god mode')
  ) return 'preworkout';
  if (s.includes('electrolyte')) return 'electrolyte';
  if (s.includes('omega'))       return 'omega';
  if (s.includes('shilajit'))    return 'shilajit';
  if (s.includes('whey'))        return 'whey';
  if (s.includes('zero sugar'))  return 'zerosugar';
  if (s.includes('ketofy') || s.includes('keto ')) return 'ketofy';
  return null;
}

export function extractWeight(sku, newSkus = {}) {
  if (newSkus[sku] && Number.isFinite(Number(newSkus[sku].weight))) {
    return Number(newSkus[sku].weight);
  }
  const matches = [...String(sku || '').matchAll(/(\d+(?:\.\d+)?)\s*(kg|g|gm|gms|grams?)\b/ig)];
  if (!matches.length) return null;
  const m = matches[0];
  const n = Number(m[1]);
  return String(m[2]).toLowerCase() === 'kg' ? n * 1000 : n;
}

export const skuCategory = (sku, newSkus = {}) =>
  newSkus[sku]?.category || normalizeCategory(sku);

// Count only positive-actual months as "usable history". Zero-filled
// pre-launch months must not make a SKU look mature.
export function ownActualMonthsBefore(sku, target, actualsOverride) {
  return ACTUAL_MONTHS.filter((m) =>
    monthIndex(m) < monthIndex(target) &&
    Number.isFinite(val(sku, m, actualsOverride)) &&
    val(sku, m, actualsOverride) > 0
  );
}

// Universal rule — sparse if fewer than 6 positive-sales months exist.
export function isNewOrSparseSku(sku, target, actualsOverride) {
  return ownActualMonthsBefore(sku, target, actualsOverride).length < 6;
}

// Combo SKU detection — bundles / multi-packs / value-packs are excluded
// from Smart Projections views + peer discovery so the engine only reasons
// about atomic SKUs. Matches:
//   • `+` anywhere        → "A + B", "A + B + Shaker"
//   • `×` anywhere        → "A × 2" (unicode multiplication)
//   • ` x2` / ` X2`       → "Zero Sugar (400g) x2"
//   • ` x 2` / ` X 2`     → "(270g) x 2"
// Underlying data is untouched — this is a display/derivation filter only.
export function isComboSku(sku) {
  if (!sku) return false;
  return /[+×]|\s[xX]\s?\d+/.test(sku);
}

// Peer discovery — same category, ±50g grammage, ≥6 positive months.
// Combo SKUs are excluded from peer discovery so bundle sales never
// distort atomic-SKU forecasts.
export function eligiblePeers(sku, target, newSkus = {}, actualsOverride) {
  const src = actualsOverride || ACTUALS;
  const category = skuCategory(sku, newSkus);
  const weight = extractWeight(sku, newSkus);
  if (!category || !Number.isFinite(weight)) return [];
  return Object.keys(src).filter((peer) => {
    if (peer === sku || isComboSku(peer)) return false;
    if (skuCategory(peer, newSkus) !== category) return false;
    const pw = extractWeight(peer, newSkus);
    if (!Number.isFinite(pw) || Math.abs(pw - weight) > 50) return false;
    const hist = ownActualMonthsBefore(peer, target, actualsOverride);
    return hist.length >= 6 && hist.some(
      (m) => Number.isFinite(val(peer, m, actualsOverride)) &&
             val(peer, m, actualsOverride) > 0
    );
  });
}

// modelVal — for mature SKUs, use own actual. For sparse/new SKUs, use
// the monthly average of eligible same-category peers within ±50g.
export function modelVal(sku, m, target, newSkus = {}, actualsOverride) {
  const own = val(sku, m, actualsOverride);
  if (!isNewOrSparseSku(sku, target, actualsOverride)) {
    return Number.isFinite(own) ? own : null;
  }
  const peers = eligiblePeers(sku, target, newSkus, actualsOverride);
  if (!peers.length) return null;
  const values = peers
    .map((p) => val(p, m, actualsOverride))
    .filter((v) => Number.isFinite(v) && v > 0);
  return values.length ? avg(values) : null;
}

// ---- Weight normalisation ---------------------------------------------------
export const WEIGHT_TOLERANCE = 0.01;
export const activeWeightTotal = (list) =>
  list.filter((p) => p.use).reduce((s, p) => s + (Number(p.weight) || 0), 0);
export const weightsValid = (list) =>
  list.some((p) => p.use) && Math.abs(activeWeightTotal(list) - 100) <= WEIGHT_TOLERANCE;

// Mutates the passed list — matches source-of-truth behaviour exactly.
export function normalizeAllWeights(list) {
  const active = list.filter((p) => p.use);
  if (!active.length) return;
  const total = active.reduce((s, p) => s + (Number(p.weight) || 0), 0);
  if (total <= 0) {
    const e = 100 / active.length;
    active.forEach((p) => { p.weight = e; });
  } else {
    active.forEach((p) => { p.weight = (Number(p.weight) || 0) * 100 / total; });
  }
  const fix = 100 - active.reduce((s, p) => s + p.weight, 0);
  active[active.length - 1].weight += fix;
}

export function roundWeightsToWhole(list) {
  const active = list.filter((p) => p.use);
  if (!active.length) return;
  active.forEach((p) => { p.weight = Math.max(0, Math.round(Number(p.weight) || 0)); });
  const fix = 100 - active.reduce((s, p) => s + p.weight, 0);
  active[active.length - 1].weight = Math.max(0, active[active.length - 1].weight + fix);
}

export function redistributeAfterEdit(list, index, newWeight) {
  const edited = list[index];
  if (!edited || !edited.use) return;
  const w = Math.max(0, Math.min(100, Number(newWeight) || 0));
  edited.weight = w;
  const others = list.filter((p, i) => p.use && i !== index);
  if (!others.length) { edited.weight = 100; return; }
  const remaining = 100 - w;
  const old = others.reduce((s, p) => s + (Number(p.weight) || 0), 0);
  if (old <= 0) {
    const e = remaining / others.length;
    others.forEach((p) => { p.weight = e; });
  } else {
    others.forEach((p) => { p.weight = (Number(p.weight) || 0) * remaining / old; });
  }
  others[others.length - 1].weight += 100 - activeWeightTotal(list);
}

// ---- Per-SKU accuracy-scored base method weights ---------------------------
// Backtests the 5 historical-average methods against the SKU's own recent
// months and returns weights inversely proportional to error.
export function baseMethodWeights(sku, target, newSkus = {}, actualsOverride) {
  const defaults = [30, 20, 20, 20, 10];
  const methods = ['last3', 'last6', 'last12', 'sameLY', 'latest'];
  const btMonths = ACTUAL_MONTHS.filter((m) => {
    if (monthIndex(m) >= monthIndex(target)) return false;
    const v = modelVal(sku, m, target, newSkus, actualsOverride);
    return Number.isFinite(v) && v > 0;
  });
  const recent = btMonths.slice(-8);
  if (recent.length < 3) return defaults.slice();

  function predict(method, m) {
    const p3 = priorMonths(m, 3);
    const p6 = priorMonths(m, 6);
    const p12 = priorMonths(m, 12);
    const a3  = avg(p3.map((x)  => modelVal(sku, x, m, newSkus, actualsOverride)).filter(Number.isFinite));
    const a6  = avg(p6.map((x)  => modelVal(sku, x, m, newSkus, actualsOverride)).filter(Number.isFinite));
    const a12 = avg(p12.map((x) => modelVal(sku, x, m, newSkus, actualsOverride)).filter(Number.isFinite));
    if (method === 'last3')  return p3.length  ? a3  : null;
    if (method === 'last6')  return p6.length  ? a6  : null;
    if (method === 'last12') return p12.length ? a12 : null;
    if (method === 'sameLY') {
      const ly = sameLY(m);
      const v = ly ? modelVal(sku, ly, m, newSkus, actualsOverride) : null;
      return Number.isFinite(v) ? v : (p12.length ? a12 : null);
    }
    if (method === 'latest') {
      const i = monthIndex(m);
      const prev = ALL_MONTHS[i - 1];
      const v = prev && ACTUAL_MONTHS.includes(prev) ? modelVal(sku, prev, m, newSkus, actualsOverride) : null;
      return Number.isFinite(v) ? v : (p3.length ? a3 : null);
    }
    return null;
  }

  const acc = methods.map((method) => {
    const errs = [];
    recent.forEach((m) => {
      const a  = modelVal(sku, m, target, newSkus, actualsOverride);
      const pr = predict(method, m);
      if (Number.isFinite(a) && a > 0 && Number.isFinite(pr)) errs.push(Math.abs(pr - a) / a);
    });
    if (!errs.length) return null;
    const mape = errs.reduce((s, e) => s + e, 0) / errs.length;
    return 1 / (0.05 + mape);
  });
  if (acc.every((a) => a === null)) return defaults.slice();

  const minAcc = Math.min(...acc.filter((a) => a !== null));
  const filled = acc.map((a) => a === null ? minAcc * 0.5 : a);
  const sum = filled.reduce((s, x) => s + x, 0);
  return filled.map((x) => x / sum * 100);
}

// ---- Smart projection assembly ---------------------------------------------
// Returns either a numeric projection (returnParams=false) or the full
// parameter list (returnParams=true) so the UI can render + edit them.
export function smartCalc(sku, target, newSkus = {}, actualsOverride, returnParams = false) {
  const p3 = priorMonths(target, 3);
  const p6 = priorMonths(target, 6);
  const p12 = priorMonths(target, 12);
  const allPrior = ACTUAL_MONTHS.filter((m) => monthIndex(m) < monthIndex(target));
  const latest = allPrior.length ? allPrior[allPrior.length - 1] : null;
  const ly = sameLY(target);

  const last3  = avg(p3.map((m)  => modelVal(sku, m, target, newSkus, actualsOverride)).filter(Number.isFinite));
  const last6  = avg(p6.map((m)  => modelVal(sku, m, target, newSkus, actualsOverride)).filter(Number.isFinite));
  const last12 = avg(p12.map((m) => modelVal(sku, m, target, newSkus, actualsOverride)).filter(Number.isFinite));
  const sameMonth = ly && Number.isFinite(modelVal(sku, ly, target, newSkus, actualsOverride))
    ? modelVal(sku, ly, target, newSkus, actualsOverride) : last12;
  const latestVal = latest && Number.isFinite(modelVal(sku, latest, target, newSkus, actualsOverride))
    ? modelVal(sku, latest, target, newSkus, actualsOverride) : last3;

  const prev3 = ALL_MONTHS.slice(Math.max(0, monthIndex(target) - 6), Math.max(0, monthIndex(target) - 3))
    .filter((m) => ACTUAL_MONTHS.includes(m))
    .map((m) => modelVal(sku, m, target, newSkus, actualsOverride))
    .filter(Number.isFinite);
  const trend = avg(prev3) > 0 ? ((last3 / avg(prev3)) - 1) * 100 : 0;
  const seasonality = last12 > 0 ? ((sameMonth / last12) - 1) * 100 : 0;

  const comparable = priorMonths(target, 5);
  const priorYear = comparable.map((m) => {
    const i = monthIndex(m);
    return i >= 12 ? modelVal(sku, ALL_MONTHS[i - 12], target, newSkus, actualsOverride) : null;
  }).filter(Number.isFinite);
  const curr = comparable.map((m) => modelVal(sku, m, target, newSkus, actualsOverride)).filter(Number.isFinite);
  const yoy = avg(priorYear) > 0 ? ((avg(curr) / avg(priorYear)) - 1) * 100 : 0;

  const bw = baseMethodWeights(sku, target, newSkus, actualsOverride);
  const list = [
    { name: 'Last 3 Months Average',   type: 'base',   smart: last3,       edit: last3,       weight: bw[0], use: true, logic: `${p3.join(', ')  || 'Available prior months'}` },
    { name: 'Last 6 Months Average',   type: 'base',   smart: last6,       edit: last6,       weight: bw[1], use: true, logic: `${p6.join(', ')  || 'Available prior months'}` },
    { name: 'Last 12 Months Average',  type: 'base',   smart: last12,      edit: last12,      weight: bw[2], use: true, logic: `${p12.join(', ') || 'Available prior months'}` },
    { name: 'Same Month Last Year',    type: 'base',   smart: sameMonth,   edit: sameMonth,   weight: bw[3], use: true, logic: ly ? `${ly}; rolling fallback if unavailable` : 'Rolling fallback' },
    { name: 'Latest Available Month',  type: 'base',   smart: latestVal,   edit: latestVal,   weight: bw[4], use: true, logic: latest || 'Latest prior actual' },
    { name: 'Seasonality Index',       type: 'driver', smart: seasonality, edit: seasonality, weight: 18,    use: true, logic: 'Same month LY vs rolling 12M' },
    { name: 'Recent 3-Month Trend',    type: 'driver', smart: trend,       edit: trend,       weight: 16,    use: true, logic: 'Latest 3M vs preceding 3M' },
    { name: 'YoY Growth Trend',        type: 'driver', smart: yoy,         edit: yoy,         weight: 14,    use: true, logic: 'Comparable recent period vs prior year' },
    { name: 'Marketing & Promotions',  type: 'driver', smart: 0,           edit: 0,           weight: 12,    use: true, logic: 'Manual business input' },
    { name: 'Channel Expansion',       type: 'driver', smart: 0,           edit: 0,           weight: 10,    use: true, logic: 'Manual business input' },
    { name: 'Price / Discount Impact', type: 'driver', smart: 0,           edit: 0,           weight: 8,     use: true, logic: 'Manual business input' },
    { name: 'Inventory Availability',  type: 'driver', smart: 0,           edit: 0,           weight: 6,     use: true, logic: 'Manual business input' },
    { name: 'Competition / Manual Risk', type: 'driver', smart: 0,         edit: 0,           weight: 6,     use: true, logic: 'Manual business input' },
  ];
  normalizeAllWeights(list);
  if (returnParams) return list;
  return calculateFromParams(list);
}

// Weighted blend of base methods, then multiplicative driver adjustment.
// Returns null if weights invalid — UI shows "LOCKED" state.
export function calculateFromParams(list) {
  if (!weightsValid(list)) return null;
  const baseW = list.filter((p) => p.use && p.type === 'base')
    .reduce((s, p) => s + (Number(p.weight) || 0), 0);
  if (baseW <= 0) return null;
  const base = list.filter((p) => p.use && p.type === 'base')
    .reduce((s, p) => s + (Number(p.edit) || 0) * ((Number(p.weight) || 0) / baseW), 0);
  const impact = list.filter((p) => p.use && p.type === 'driver')
    .reduce((s, p) => s + base * ((Number(p.edit) || 0) / 100) * ((Number(p.weight) || 0) / 100), 0);
  return Math.max(0, base + impact);
}

// ---- Dynamic channel split from fed history --------------------------------
// For a given SKU + target month, look back at the latest 3 fed months
// where channel-wise breakdown was captured. Compute per-month channel
// share (channel units / SKU-total units), average across those months,
// then normalise to exactly 100%. If no fed history exists, fall back to
// the passed defaults.
//
// channelActuals shape: { [sku]: { [month]: { total, channels: {name: units} } } }
// channels shape:        [[label, defaultPct], ...]
export function computeChannelSplit(sku, target, channelActuals, channels) {
  const defaults = channels.map((c) => Number(c[1]) || 0);
  if (!sku || !target || !channelActuals?.[sku]) return defaults.slice();

  // Find fed months strictly before target that carry channel data.
  const fedMonthsWithChannels = Object.entries(channelActuals[sku])
    .filter(([m, v]) => monthIndex(m) < monthIndex(target)
      && v && v.channels && v.total > 0 && Object.keys(v.channels).length > 0);
  if (!fedMonthsWithChannels.length) return defaults.slice();

  // Sort chronologically by ALL_MONTHS order and take the latest 3.
  fedMonthsWithChannels.sort((a, b) => monthIndex(a[0]) - monthIndex(b[0]));
  const recent = fedMonthsWithChannels.slice(-3);

  // Per-month channel share, then average.
  const perChannelSum = channels.map(() => 0);
  recent.forEach(([, v]) => {
    channels.forEach((c, i) => {
      const chUnits = Number(v.channels[c[0]]) || 0;
      perChannelSum[i] += chUnits / v.total * 100;
    });
  });
  const avgPct = perChannelSum.map((s) => s / recent.length);

  // Normalise so channel sum is exactly 100 (accounts for float drift and
  // channels present in defaults but absent in fed data).
  const total = avgPct.reduce((s, v) => s + v, 0);
  if (total <= 0) return defaults.slice();
  const normalised = avgPct.map((v) => v * 100 / total);
  const fix = 100 - normalised.reduce((s, v) => s + v, 0);
  normalised[normalised.length - 1] += fix;
  return normalised;
}

// ---- Predictive forecast for sparse / new SKUs -----------------------------
// Category-level growth assumptions (annualised, D2C benchmarks). Ops can
// override any value via the newSkus[sku].categoryGrowth field.
export const CATEGORY_GROWTH_YOY = {
  creatine:    0.35, // 35% category growth
  preworkout:  0.28,
  electrolyte: 0.42,
  omega:       0.18,
  shilajit:    0.22,
  whey:        0.12,
  zerosugar:   0.15,
  ketofy:      0.10,
};

// Returns a rich diagnostic object so the UI can explain the number.
//   { units, trendPct, ownWeight, peerAvg, peerGrowth, seasonality, categoryLift,
//     references: [{sku, weight, contribution}], notes: [string], usable: bool }
// If no eligible peers exist, units = 0 (matches existing sparse rule) and
// notes explain why. Existing smartCalc is untouched — this is additive.
export function predictSparseSku(sku, target, newSkus = {}, actualsOverride, channelActuals = {}) {
  const notes = [];
  const category = skuCategory(sku, newSkus);
  const weight = extractWeight(sku, newSkus);
  const ownHistory = ownActualMonthsBefore(sku, target, actualsOverride);
  const ownMonths = ownHistory.length;

  // Own maturity: 0 at fresh launch → 1 at ≥6 months of positive sales.
  const ownWeight = Math.min(1, ownMonths / 6);

  const peers = eligiblePeers(sku, target, newSkus, actualsOverride);
  if (!peers.length) {
    notes.push('No eligible peers within same category ±50g with ≥6 months history.');
    if (category && Number.isFinite(weight)) {
      notes.push(`Add reference SKUs for '${category}' near ${weight}g so the engine can forecast.`);
    }
    return {
      units: 0, trendPct: 0, ownWeight, peerAvg: 0, peerGrowth: 0,
      seasonality: 1, categoryLift: 1, references: [], notes, usable: false,
      category, weight,
    };
  }

  // Peer per-SKU trailing 3-month average (or fewer if newer).
  const p3 = priorMonths(target, 3);
  const p6 = priorMonths(target, 6);
  const perPeer = peers.map((peer) => {
    const recent = p3.map((m) => val(peer, m, actualsOverride)).filter((v) => Number.isFinite(v) && v > 0);
    const mid = p6.slice(0, Math.max(0, p6.length - 3))
      .map((m) => val(peer, m, actualsOverride)).filter((v) => Number.isFinite(v) && v > 0);
    return {
      sku: peer,
      recentAvg: recent.length ? avg(recent) : 0,
      priorAvg: mid.length ? avg(mid) : 0,
    };
  });
  const peerAvg = avg(perPeer.map((p) => p.recentAvg).filter((v) => v > 0));
  const peerPriorAvg = avg(perPeer.map((p) => p.priorAvg).filter((v) => v > 0));
  const peerGrowth = peerPriorAvg > 0 ? (peerAvg / peerPriorAvg - 1) : 0;

  // Seasonality: same-month-LY average / trailing 12M average across peers.
  const ly = sameLY(target);
  let seasonality = 1;
  if (ly) {
    const lyVals = peers.map((p) => val(p, ly, actualsOverride)).filter((v) => Number.isFinite(v) && v > 0);
    const p12 = priorMonths(target, 12);
    const trailing = peers.flatMap((p) => p12.map((m) => val(p, m, actualsOverride))).filter((v) => Number.isFinite(v) && v > 0);
    if (lyVals.length && trailing.length) {
      const ratio = avg(lyVals) / avg(trailing);
      if (Number.isFinite(ratio) && ratio > 0) seasonality = Math.max(0.4, Math.min(2.0, ratio));
    }
  }

  // Category growth lift — annualised, scaled to the number of months
  // between the peers' latest month and the target month (typically 1–3).
  const rawCat = Number.isFinite(newSkus[sku]?.categoryGrowth)
    ? Number(newSkus[sku].categoryGrowth)
    : (CATEGORY_GROWTH_YOY[category] || 0);
  const monthsAhead = Math.max(1, ALL_MONTHS.indexOf(target) - (ACTUAL_MONTHS.length - 1));
  const categoryLift = Math.pow(1 + rawCat, monthsAhead / 12);

  const peerProjection = peerAvg * (1 + Math.max(-0.5, Math.min(1.0, peerGrowth))) * seasonality * categoryLift;

  // Blend with own smart projection as SKU matures.
  const ownSmart = ownMonths > 0 ? smartCalc(sku, target, newSkus, actualsOverride) : 0;
  const units = Math.max(0, Math.round((1 - ownWeight) * peerProjection + ownWeight * ownSmart));

  // Per-peer contribution for transparency: weight = recentAvg / sum.
  const contribBase = perPeer.reduce((s, p) => s + (p.recentAvg || 0), 0);
  const references = perPeer.map((p) => ({
    sku: p.sku,
    weight: contribBase > 0 ? p.recentAvg / contribBase : 1 / perPeer.length,
    contribution: Math.round(peerProjection * (contribBase > 0 ? p.recentAvg / contribBase : 1 / perPeer.length) * (1 - ownWeight)),
  })).sort((a, b) => b.weight - a.weight).slice(0, 5);

  if (ownMonths > 0 && ownMonths < 6) notes.push(`SKU has ${ownMonths} month(s) own history — blended at ${(ownWeight * 100).toFixed(0)}% own · ${((1 - ownWeight) * 100).toFixed(0)}% peer.`);
  if (Math.abs(peerGrowth) > 0.05) notes.push(`Peer 3-month trend: ${peerGrowth >= 0 ? '+' : ''}${(peerGrowth * 100).toFixed(1)}%.`);
  if (Math.abs(seasonality - 1) > 0.05) notes.push(`Seasonality index ${seasonality.toFixed(2)}× (peer LY-month vs peer trailing 12M).`);
  if (rawCat > 0) notes.push(`Category growth assumption ${(rawCat * 100).toFixed(0)}% YoY → ${monthsAhead}-month lift ${((categoryLift - 1) * 100).toFixed(1)}%.`);

  return {
    units, trendPct: peerGrowth * 100, ownWeight, peerAvg, peerGrowth,
    seasonality, categoryLift, references, notes, usable: true,
    category, weight,
  };
}
